/**
 * Compose multiple React providers into a single component
 * This utility helps avoid deep nesting of providers
 *
 * @example
 * ```tsx
 * const AllProviders = composeProviders([
 *   [ThemeProvider, { theme: 'dark' }],
 *   [AuthProvider],
 *   [DataProvider, { apiUrl: '/api' }],
 * ]);
 *
 * function App() {
 *   return (
 *     <AllProviders>
 *       <YourApp />
 *     </AllProviders>
 *   );
 * }
 * ```
 */

import type { ComponentType, ReactNode } from 'react';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyComponent = ComponentType<any>;

// Extract props from provider, excluding children
type ProviderProps<C> =
  C extends ComponentType<infer P> ? Omit<P, 'children'> : never;

// Check if value V is assignable to Expected[K], using covariant check for arrays
type IsAssignable<V, Target> = V extends Target ? true : false;

// Check a single prop: returns the expected type if invalid (to show in error)
type CheckProp<K extends PropertyKey, V, Expected> = K extends keyof Expected
  ? IsAssignable<V, Expected[K]> extends true
    ? V
    : Expected[K] // Return expected type to show what's required
  : `[Unknown prop: ${K & string}]`;

// Check all props
type CheckProps<P, Expected> = {
  [K in keyof P]: CheckProp<K, P[K], Expected>;
};

// Validate a provider entry tuple
type ValidateEntry<T> = T extends [infer C extends AnyComponent, infer P]
  ? [C, CheckProps<P, ProviderProps<C>>]
  : T extends [infer C extends AnyComponent]
    ? [C]
    : never;

// Provider entry: [Component] or [Component, Props]
type ProviderEntry = [AnyComponent] | [AnyComponent, Record<string, unknown>];

/**
 * Compose multiple providers into a single component
 * @param providers - Array of [Provider, props?] tuples with full type inference
 * @returns A composed provider component
 */
export function composeProviders<const T extends readonly ProviderEntry[]>(
  providers: { [K in keyof T]: ValidateEntry<T[K]> } & T,
): ComponentType<{ children: ReactNode }> {
  return ({ children }: { children: ReactNode }) => {
    return providers.reduceRight((acc, entry) => {
      const [ProviderComponent, props = {}] = entry;
      return <ProviderComponent {...props}>{acc}</ProviderComponent>;
    }, children as React.ReactElement);
  };
}
